import { MbeClose } from '@/components/icons';
import { CtaLoader, MbeInput } from '@/components/shared';
import {
  Button,
  Flex,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalHeader,
  ModalOverlay,
} from '@chakra-ui/react';
import { useForm } from 'react-hook-form';
import type { SubmitHandler } from 'react-hook-form';
import type { ISetNewPhoneNumber, IBaseForm } from '@/types';
import { useTranslation } from 'react-i18next';
import { ERRORS, phoneRegex } from '@/constants';
import { hasEmptyFields, hasFormAnyError } from '@/utils';

interface ISettingsPhoneModalProps extends IBaseForm<ISetNewPhoneNumber> {
  isLoading: boolean;
  phoneNumber: string | undefined;
}

export const SettingsPhoneModal: React.FC<ISettingsPhoneModalProps> = ({
  isLoading,
  onSubmit: providedOnSubmit,
  phoneNumber,
}): JSX.Element => {
  const { t } = useTranslation();

  const {
    formState: { errors },
    handleSubmit: submitHandler,
    register,
    watch,
  } = useForm<ISetNewPhoneNumber>({
    mode: 'all',
    criteriaMode: 'all',
    defaultValues: { newPhoneNumber: phoneNumber },
  });

  const submitNewPhoneNumberHandler: SubmitHandler<ISetNewPhoneNumber> = (
    newPhoneNumber,
    event
  ): void => {
    event?.preventDefault();
    providedOnSubmit?.(newPhoneNumber);
  };

  const areRequiredFieldsEmpty = hasEmptyFields(watch());
  const isDisabled = hasFormAnyError(errors) || areRequiredFieldsEmpty;

  const ctaButton = isLoading ? <CtaLoader /> : t('ACCOUNT.SETTINGS.CONFIRM');

  return (
    <>
      <ModalOverlay backdropFilter="blur(10px) hue-rotate(90deg)" bg="grey" />
      <ModalContent borderRadius={0} minH="80%" minW={{ base: '50%' }}>
        <ModalBody overflow="hidden" p={0} paddingInlineEnd={0} paddingInlineStart={0}>
          <Flex
            alignItems="center"
            justifyContent="flex-end"
            p={4}
            position="absolute"
            w="full"
            zIndex={3}
          >
            <ModalCloseButton
              _hover={{ bgColor: 'white' }}
              bgColor="white"
              borderRadius="full"
              h={10}
              left={0}
              position="relative"
              top={0}
              w={10}
            >
              <MbeClose h="3rem" w="3rem" />
            </ModalCloseButton>
          </Flex>
          <Flex
            alignItems="center"
            direction="column"
            h="full"
            justifyContent="center"
            position={{ base: 'absolute' }}
            px={{ base: '5%', md: '0' }}
            w={{ base: 'full' }}
            zIndex={2}
          >
            <ModalHeader fontSize={{ base: '24px', md: '32px' }} fontWeight="normal" mb={8}>
              {t('ACCOUNT.SETTINGS.MODIFY_PHONE.TITLE')}
            </ModalHeader>
            <form
              autoComplete="off"
              id="newPhoneNumberForm"
              name="newPhoneNumberForm"
              style={{
                display: 'flex',
                width: '100%',
              }}
              // eslint-disable-next-line @typescript-eslint/no-misused-promises
              onSubmit={submitHandler(submitNewPhoneNumberHandler)}
            >
              <Flex
                alignItems="center"
                direction="column"
                gap={12}
                justifyContent="center"
                mx={{ base: '5%', md: '25%' }}
                w="100%"
              >
                <MbeInput
                  error={errors.newPhoneNumber}
                  id="newPhoneNumber"
                  mx="auto"
                  placeholder={t('ACCOUNT.SETTINGS.MODIFY_PHONE.INPUT_PHONE_NUMBER')}
                  {...register('newPhoneNumber', {
                    required: { value: true, message: t(ERRORS.empty) },
                    pattern: { value: phoneRegex, message: t(ERRORS.phoneNumber) },
                  })}
                />
                <Button disabled={isDisabled} mx="auto" type="submit" variant="mbePrimary" w="100%">
                  {ctaButton}
                </Button>
              </Flex>
            </form>
          </Flex>
        </ModalBody>
      </ModalContent>
    </>
  );
};
